/**
 *
 * The location change duck.  It lets us know when the user navigates.
 *
 * Action Types:
 * - UPDATE_ROUTE
 * - ROUTER_LOCATION_CHANGED
 *
 * Action Creators:
 * - routerLocationChanged
 */

import { hasDifferentRoute, urlSplitter } from '../utils/urls/urlComparer';
import { removeQueryAndHash } from '../utils/urls/urlGenerator';
import {
  PATH_TYPE_MAP,
  LANDING_PAGE,
  FLYER_PAGE,
  CARD_PAGE,
  LEGAL_PAGE,
  ERROR_PAGE,
} from '../../routes';

/** @type {ActionType} */
export const UPDATE_ROUTE = 'PB/location/UPDATE_ROUTE';
export const ROUTER_LOCATION_CHANGED = 'PB/location/ROUTER_LOCATION_CHANGED';

export const initialState = {
  currentRoute: {
    href: undefined,
    // TODO: Change to 'routerPathName'?
    pathname: undefined,
    asPathPathname: undefined,
    pathType: undefined,
    slug: undefined,
    query: undefined,
    hash: undefined,
  },
  previousRoute: undefined,
};

/** @ignore */
export default function route(state = initialState, action) {
  switch (action.type) {
    case UPDATE_ROUTE:
      // `slug` is not available on every route change, so persist it until slug or pathname changes...
      let slug = action.payload.slug;
      if (!slug && state.currentRoute.pathname === action.payload.pathname) {
        slug = state.currentRoute.slug;
      }

      let newRoute = { ...action.payload, slug };

      // Only update the route if its actually new.  Can get duplicated updates when switching the route
      // and getInitialProps is called, followed by routeChangeComplete.
      if (hasDifferentRoute(state.currentRoute, newRoute, [])) {
        return {
          previousRoute: state.currentRoute,
          currentRoute: newRoute,
        };
      }
      return { ...state };

    default:
      return state;
  }
}

// NOTE: We cannot just `import Router from 'next/router';` as it isn't ready on the server.
// NOTE: DON'T LISTEN TO THIS IF ROUTER_LOCATION_CHANGED CAN WORK INSTEAD
// This updates the router location in the route duck before the route epic communicates
// the new information the rest of the app.  This ensures the store is updated and has the
// nev information available when ROUTER_LOCATION_CHANGED is triggered.
export function updateRoute(href: string, router: any) {
  const {
    pathname,
    asPath,
    query,
    query: { slug = undefined },
  } = router;

  const pathType = PATH_TYPE_MAP[pathname] || ERROR_PAGE;
  const splitURLObj = urlSplitter(href);

  return {
    type: UPDATE_ROUTE,
    payload: {
      href: splitURLObj.href,
      pathname,
      asPathPathname: removeQueryAndHash(asPath),
      pathType,
      slug,
      query,
      hash: splitURLObj.hash,
    },
  };
}

// NOTE: We cannot just `import Router from 'next/router';` as it isn't ready on the server.
// This action tells all the other parts of the application that the route has changed.
export function routerLocationChanged(currentRoute: any) {
  return {
    type: ROUTER_LOCATION_CHANGED,
    payload: {
      ...currentRoute,
    },
  };
}
